import {
  ArrowDownRight, ArrowUpRight, Ban, Clock3, MoveHorizontal, TrendingDown, TrendingUp,
} from "lucide-react";
import { isNum, num } from "./fmt";
import { blockerWords, combinedVerdict, MAGNITUDE_BLOCKERS, payState, primaryBlocker } from "./VerdictCard";

/*
 * SIMPLE SIGNAL — the dashboard in two words (2026-10-05), now the pure
 * readers behind the Buyer Funnel's fourth gate and its verdict line.
 *
 *   REGIME   UP / DOWN / NEUTRAL      (trend-regime engine on closed 5-minute
 *                                      bars; the signal's direction when the
 *                                      engine has no answer)
 *   ACTION   BUY CALL / BUY PUT / NO TRADE   (the one combined verdict; its
 *                                      "WAIT" is shown as NO TRADE)
 *
 * plus one plain sentence of why. Decision support: the live rules trade on
 * their own conditions, not on this reading.
 */

/* Regime in one word. */
export function simpleRegime(trend, view) {
  const closed = Boolean(view?.sessionClosed);
  const state = trend?.available ? trend.state : null;
  const mins = isNum(trend?.minutes_in_state) ? Number(trend.minutes_in_state) : null;
  const held = closed ? "At the last close." : mins != null ? `For the last ${num(mins)} min.` : "";
  if (state === "UP") return { word: "UP", tone: "emerald", Icon: TrendingUp, detail: `Market is trending up. ${held}`.trim() };
  if (state === "DOWN") return { word: "DOWN", tone: "rose", Icon: TrendingDown, detail: `Market is trending down. ${held}`.trim() };
  if (state === "SIDEWAYS") return { word: "NEUTRAL", tone: "amber", Icon: MoveHorizontal, detail: `Market is going sideways. ${held}`.trim() };
  // Engine has no answer (warming up / offline): fall back to the signal's lean.
  if (closed) return { word: "—", tone: "slate", Icon: Clock3, detail: "Read only while the market is open." };
  const d = view?.direction;
  if (d === "BULLISH") return { word: "UP", tone: "emerald", Icon: TrendingUp, detail: "Price is above its average and leaning up." };
  if (d === "BEARISH") return { word: "DOWN", tone: "rose", Icon: TrendingDown, detail: "Price is below its average and leaning down." };
  if (d === "NO DATA") return { word: "—", tone: "slate", Icon: MoveHorizontal, detail: "Waiting for live data." };
  return { word: "NEUTRAL", tone: "amber", Icon: MoveHorizontal, detail: "No clear direction yet." };
}

/* Action in one of three answers, with one plain sentence of why. */
export function simpleAction(edge, view, regimeWord) {
  const v = combinedVerdict(edge, view);
  if (v.word === "BUY CALL" || v.word === "BUY PUT") {
    const call = v.word === "BUY CALL";
    // The regime is context (direction reads are right ~50-55% of the time and
    // regime gates were rejected in testing) — it is named, never a veto.
    const against = (call && regimeWord === "DOWN") || (!call && regimeWord === "UP");
    const pay = payState(edge);
    const why = v.tone === "amber" ? "Direction is clear, but the market is not moving much. Use small size."
      : pay === "breakeven" ? `Market is moving and leaning ${call ? "up" : "down"}; the move covers charges but the usual target is a stretch.`
        : `Market is moving enough and leaning ${call ? "up" : "down"}.${against ? ` The longer trend reads ${regimeWord} — context only.` : ""}`;
    return { word: v.word, tone: v.tone, Icon: call ? ArrowUpRight : ArrowDownRight, why, caution: false, plan: view?.tradePlan || null };
  }
  let why = "No clear setup yet. Wait.";
  const first = primaryBlocker(edge);
  if (view?.sessionClosed) why = view.closedLabel === "Market holiday" ? "Market holiday today." : "Market is closed.";
  else if (view?.health?.ready === false) why = "Live data is not ready.";
  else if (edge?.verdict?.edge === false && first && !MAGNITUDE_BLOCKERS.has(first)) why = `${blockerWords(first).replace(/^./, (c) => c.toUpperCase())}.`;
  else if (edge?.verdict?.edge === false && first === "PREMIUM_NOT_COVERED") why = "Today's expected move is too small to pay the option's charges.";
  else if (edge?.verdict?.edge === false) why = "Market is too quiet. Option buyers lose on quiet days.";
  else if (view?.action?.side === "AVOID") why = "Conditions are risky right now.";
  else if (v.word === "WAIT") why = v.qualifier?.includes("gate not measured") ? "Price has picked a side, but movement is not measured yet. Wait." : "Market is moving, but it has not picked a side yet. Wait.";
  return { word: "NO TRADE", tone: view?.sessionClosed ? "slate" : "amber", Icon: view?.sessionClosed ? Clock3 : Ban, why, caution: false, plan: null };
}
